// src/oura/visibility/useQuestionTrendScans.ts
//
// The earlier scans the Questions table's trend arrows compare against (#4125).
//
// NOT the Competitors tab's "previous comparable scan". That one needs the
// whole 50 to be identical and gives up the moment the newest scan is a break,
// which is right for a rival count and far too strict for one question: a
// question whose text is the same in two scans is the same measurement, even
// when other slots were swapped between them. So this loads the last few
// complete scans, whatever their question set, and each cell is compared with
// the newest one that asked THAT question (questionTrend.ts).
//
// The one thing that makes two scans of one question incomparable is a change
// of ChatGPT instrument (the app and the API read differently), which the
// history records per scan, so it is checked there, per engine.
//
// The history carries only aggregates, so the questions of an older scan come
// from `GET /assessments/{id}`, through the same builder the screen uses. At
// most TREND_SCANS requests, in parallel, only while the Questions tab is open.

import { useEffect, useState } from "react";
import { getAssessment, type AeoScoreHistoryEntry } from "@service/aeo";
import { buildVisModel, visibilityInputsFromAssessment, type LatestAssessment, type VisibilityInputs } from "./fromLive";
import { priorScanFrom, type VisPriorScan } from "./rivalFacts";

/** How many earlier scans to read. Each is one full assessment. */
export const TREND_SCANS = 3;

export interface TrendScan {
    prior: VisPriorScan;
    /** The ChatGPT instrument that scan used, or null when it did not record one. */
    instrument: string | null;
}

export type TrendScansStatus = "none" | "loading" | "ready" | "error";

export interface TrendScansState {
    status: TrendScansStatus;
    /** Newest first. Only the scans that loaded. */
    scans: TrendScan[];
    /** The ChatGPT instrument of the scan on screen, or null when unknown. */
    nowInstrument: string | null;
}

/** The ChatGPT instrument a history entry recorded. */
export function scanInstrument(h: AeoScoreHistoryEntry | null | undefined): string | null {
    return h?.visibility?.engines.chatgpt?.instrument ?? null;
}

/** The earlier complete scans worth reading, newest first. */
export function trendCandidates(
    history: AeoScoreHistoryEntry[],
    assessmentId: string | null,
    limit: number = TREND_SCANS,
): AeoScoreHistoryEntry[] {
    const now = history.find((h) => h.assessment_id === assessmentId);
    if (!now) return [];
    return history
        .filter((h) => h.assessment_id !== now.assessment_id)
        .filter((h) => (h.status ?? "complete") === "complete" && !h.gated && !!h.visibility)
        .filter((h) => h.captured_at < now.captured_at)
        .sort((a, b) => b.captured_at.localeCompare(a.captured_at))
        .slice(0, limit);
}

const NONE: TrendScansState = { status: "none", scans: [], nowInstrument: null };

export function useQuestionTrendScans({
    inputs,
    fixture,
    enabled,
}: {
    inputs: VisibilityInputs | null;
    /** A sample business carries its previous scan in the fixture. */
    fixture: boolean;
    /** Only while the Questions tab is open. */
    enabled: boolean;
}): TrendScansState {
    const nowEntry = inputs?.history.find((h) => h.assessment_id === inputs.assessmentId) ?? null;
    const nowInstrument = scanInstrument(nowEntry);
    const candidates = !fixture && inputs ? trendCandidates(inputs.history, inputs.assessmentId) : [];
    // Keyed by the scans it belongs to, so a change of target never shows the
    // old scans' result for one render under the new scan.
    const key = candidates.map((c) => c.assessment_id).join(",");
    const [loaded, setLoaded] = useState<{ key: string; status: TrendScansStatus; scans: TrendScan[] }>({
        key: "",
        status: "none",
        scans: [],
    });

    useEffect(() => {
        if (fixture || !enabled || !inputs || candidates.length === 0) {
            setLoaded({ key: "", status: "none", scans: [] });
            return;
        }
        let live = true;
        setLoaded({ key, status: "loading", scans: [] });
        Promise.all(
            candidates.map((c) =>
                getAssessment(c.assessment_id)
                    .then((a): TrendScan | null => {
                        const past = visibilityInputsFromAssessment(a as unknown as LatestAssessment, {
                            capturedAt: c.captured_at,
                            history: inputs.history,
                        });
                        if (!past.vis) return null;
                        return { prior: priorScanFrom(buildVisModel(past, null)), instrument: scanInstrument(c) };
                    })
                    // One scan failing leaves the others usable; a thrown model
                    // build lands here too.
                    .catch(() => null),
            ),
        ).then((all) => {
            if (!live) return;
            const scans = all.filter((s): s is TrendScan => s !== null);
            setLoaded({ key, status: scans.length > 0 ? "ready" : "error", scans });
        });
        return () => { live = false; };
        // `candidates` and `inputs` are read for the chosen ids only; the key decides.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fixture, enabled, key]);

    if (fixture) {
        const prior = inputs?.prior ?? null;
        return prior ? { status: "ready", scans: [{ prior, instrument: null }], nowInstrument: null } : NONE;
    }
    if (!enabled || candidates.length === 0) return { ...NONE, nowInstrument };
    if (loaded.key !== key) return { status: "loading", scans: [], nowInstrument };
    return { status: loaded.status, scans: loaded.scans, nowInstrument };
}
